import React, { useState } from 'react';
import { FileDown, Clock } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AmbienceSound } from './AmbienceMixer';

interface DownloadButtonProps {
  presetName: string;
  onDirectDownload: (
    presetName: string, 
    frequency: number, 
    duration: number, 
    ambientSounds?: AmbienceSound[]
  ) => Promise<void>;
  frequency: number;
  duration?: number;
  ambientSounds?: AmbienceSound[];
}

const DownloadButton: React.FC<DownloadButtonProps> = ({ 
  presetName,
  onDirectDownload,
  frequency,
  duration = 0,
  ambientSounds = []
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Get active ambient sounds (volume > 0)
  const activeAmbientSounds = ambientSounds.filter(sound => sound.volume > 0);
  
  const handleDirectDownload = async (durationMinutes: number) => {
    // Show premium message as a toast instead of dialog
    toast.info("Downloads coming soon! This will be a premium feature.", {
      duration: 3000,
    });
  };
  
  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-sm font-medium shadow-sm transition-all hover:shadow-md hover:bg-primary/5 dark:hover:bg-primary/10"
            disabled={isGenerating}
          >
            <FileDown size={16} className="text-primary" />
            {isGenerating ? "Generating..." : "Download WAV"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-40 bg-popover border border-border shadow-md rounded-md">
          <DropdownMenuGroup>
            <DropdownMenuItem 
              onClick={() => handleDirectDownload(15)}
              className="flex items-center hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer opacity-70"
            >
              <Clock className="mr-2 h-4 w-4 text-primary" />
              <span>15 minutes</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleDirectDownload(30)}
              className="flex items-center hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer opacity-70"
            >
              <Clock className="mr-2 h-4 w-4 text-primary" />
              <span>30 minutes</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleDirectDownload(60)}
              className="flex items-center hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer opacity-70"
            >
              <Clock className="mr-2 h-4 w-4 text-primary" />
              <span>60 minutes</span>
            </DropdownMenuItem>
            <DropdownMenuItem 
              onClick={() => handleDirectDownload(-1)}
              className="flex items-center hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer opacity-70"
            >
              <Clock className="mr-2 h-4 w-4 text-primary" />
              <span>Custom...</span>
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default DownloadButton;
